import React, { useState, useEffect, useCallback } from 'react';
import { portfolioAPI } from '../utils/api';
import { updatePageMeta, addStructuredData, addBreadcrumbSchema } from '../utils/seo';
import { metaFor } from '../seo/routeMeta';
import { sizedImage, fallbackToOriginal } from '../utils/cloudinary';
import '../App.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

// Data-driven image SEO: alt text + ImageObject schema are derived from each
// item's stored fields (title, category, mediaType) at render time, so EVERY
// future portfolio image inherits descriptive, keyworded alt automatically —
// no per-image work and no code revisit when new images are added.
const CATEGORY_LABELS = {
  residential: 'residential',
  commercial: 'commercial',
  luxury: 'luxury',
  aerial: 'aerial / drone',
  twilight: 'twilight',
  video: '',
  other: ''
};

const buildAltText = (item, image, position) => {
  if (!item) return 'Utah real estate photography by Utah Realty Photos';
  const label = CATEGORY_LABELS[item.category] != null ? CATEGORY_LABELS[item.category] : '';
  const medium = image && image.mediaType === 'video' ? 'video' : 'photography';
  const desc = `${item.title} — ${label ? label + ' ' : ''}real estate ${medium} in Utah`;
  return position ? `${desc} (${position})` : desc;
};

function Portfolio() {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [allItems, setAllItems] = useState([]); // Store all items for category extraction
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxTitle, setLightboxTitle] = useState('');
  const [lightboxItem, setLightboxItem] = useState(null);

  useEffect(() => {
    updatePageMeta(metaFor('/portfolio'));
    addBreadcrumbSchema([
      { name: 'Home', url: 'https://www.utahrealtyphotos.com/' },
      { name: 'Portfolio', url: 'https://www.utahrealtyphotos.com/portfolio' }
    ]);
  }, []);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  // Filter client-side when category changes
  useEffect(() => {
    if (selectedCategory === 'all') {
      setPortfolioItems(allItems);
    } else {
      setPortfolioItems(allItems.filter(item => item.category === selectedCategory));
    }
  }, [selectedCategory, allItems]);

  // Emit ImageGallery / ImageObject structured data for the portfolio, derived
  // from the live items — so new images are included automatically. Videos are
  // skipped (an incomplete VideoObject can trigger Search Console errors).
  useEffect(() => {
    if (!allItems || allItems.length === 0) return;
    const images = [];
    allItems.forEach((item) => {
      (item.images || []).forEach((img) => {
        if (isVideo(img)) return;
        const url = getImageUrl(img);
        if (!url) return;
        images.push({
          '@type': 'ImageObject',
          contentUrl: url,
          url,
          name: item.title,
          description: buildAltText(item, img),
          creator: { '@type': 'Organization', name: 'Utah Realty Photos' }
        });
      });
    });
    if (images.length === 0) return;
    addStructuredData({
      '@context': 'https://schema.org',
      '@type': 'ImageGallery',
      name: 'Utah Realty Photos — Real Estate Photography Portfolio',
      description: 'Sample real estate photography across Utah: residential, commercial, luxury, aerial/drone, and twilight.',
      url: 'https://www.utahrealtyphotos.com/portfolio',
      image: images.slice(0, 50)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems]);

  // Keyboard navigation for lightbox
  const handleKeyDown = useCallback((e) => {
    if (!lightboxOpen) return;
    if (e.key === 'Escape') setLightboxOpen(false);
    if (e.key === 'ArrowRight') setLightboxIndex(i => (i + 1) % lightboxImages.length);
    if (e.key === 'ArrowLeft') setLightboxIndex(i => (i - 1 + lightboxImages.length) % lightboxImages.length);
  }, [lightboxOpen, lightboxImages.length]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown, lightboxOpen]);

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      // Always fetch all items so we can build dynamic category tabs
      const response = await portfolioAPI.getAll();
      const items = response.data?.data || response.data || [];
      setAllItems(items);
      // Filter client-side based on selected category
      if (selectedCategory === 'all') {
        setPortfolioItems(items);
      } else {
        setPortfolioItems(items.filter(item => item.category === selectedCategory));
      }
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return '';
    if (image.url && (image.url.startsWith('http://') || image.url.startsWith('https://'))) {
      return image.url;
    }
    return `${API_BASE}${image.url}`;
  };

  const isVideo = (media) => {
    if (!media) return false;
    if (media.mediaType === 'video') return true;
    if (media.embedUrl) return true;
    const url = (media.url || '').toLowerCase();
    return url.endsWith('.mp4') || url.endsWith('.mov') || url.endsWith('.webm') || url.includes('/video/');
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    // YouTube — supports watch, youtu.be, embed, and Shorts URLs
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  // Build a poster/thumbnail URL for an embedded video so we don't try to render
  // a YouTube/Vimeo URL inside an HTML5 <video> element (which throws NotSupportedError).
  const getEmbedThumbnail = (url) => {
    if (!url) return null;
    // YouTube high-quality thumbnail
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://i.ytimg.com/vi/${ytMatch[1]}/hqdefault.jpg`;
    // Vimeo doesn't expose a deterministic thumbnail without an API call —
    // fall back to null so we render a placeholder play-icon panel.
    return null;
  };

  const openLightbox = (item, startIndex = 0) => {
    if (!item.images || item.images.length === 0) return;
    setLightboxImages(item.images);
    setLightboxIndex(startIndex);
    setLightboxTitle(item.title);
    setLightboxItem(item);
    setLightboxOpen(true);
  };

  const closeLightbox = (e) => {
    if (e.target === e.currentTarget) {
      setLightboxOpen(false);
    }
  };

  // Build dynamic categories from actual data
  const categories = ['all', ...Array.from(new Set(allItems.map(item => item.category).filter(Boolean))).sort()];

  return (
    <div style={{ paddingTop: '140px', minHeight: '100vh' }}>
      <div className="container">
        <div className="section-title">
          <h1>Portfolio</h1>
          <p>Explore our professional real estate photography</p>
        </div>

        {/* Category Filter */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '40px', flexWrap: 'wrap' }}>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: '10px 20px',
                border: selectedCategory === category ? '2px solid #667eea' : '2px solid #ddd',
                background: selectedCategory === category ? '#667eea' : 'white',
                color: selectedCategory === category ? 'white' : '#333',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                textTransform: 'capitalize',
                transition: 'all 0.3s'
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>Loading portfolio...</p>
          </div>
        ) : portfolioItems.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '50px' }}>
            <p>No portfolio items found. Check back soon!</p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(400px, 100%), 1fr))',
            gap: '30px',
            marginBottom: '50px'
          }}>
            {portfolioItems.map(item => (
              <div 
                key={item._id}
                className="card"
                style={{
                  overflow: 'hidden',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  cursor: 'pointer'
                }}
                onClick={() => openLightbox(item, 0)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                {item.images && item.images.length > 0 && (
                  <div style={{ position: 'relative' }}>
                    {isVideo(item.images[0]) ? (
                      // Embedded video (YouTube/Vimeo) — render a poster, not <video>.
                      // <video src=youtube-url> throws NotSupportedError.
                      item.images[0].embedUrl ? (
                        getEmbedThumbnail(item.images[0].embedUrl) ? (
                          <img
                            src={getEmbedThumbnail(item.images[0].embedUrl)}
                            alt={buildAltText(item, item.images[0])}
                            style={{
                              width: '100%',
                              height: '300px',
                              objectFit: 'cover',
                              backgroundColor: '#f5f5f5',
                              display: 'block'
                            }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '300px',
                            backgroundColor: '#1a1a1a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <span style={{ color: '#fff', fontSize: '14px', opacity: 0.6 }}>Video</span>
                          </div>
                        )
                      ) : (
                        // Direct video file — safe to use <video>
                        <video
                          src={getImageUrl(item.images[0])}
                          muted
                          playsInline
                          onMouseEnter={e => { e.target.play().catch(() => {}); }}
                          onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                          style={{
                            width: '100%',
                            height: '300px',
                            objectFit: 'cover',
                            backgroundColor: '#f5f5f5',
                            display: 'block'
                          }}
                        />
                      )
                    ) : (
                      <img
                        src={sizedImage(getImageUrl(item.images[0]), 800)}
                        loading="lazy"
                        decoding="async"
                        onError={fallbackToOriginal(getImageUrl(item.images[0]))}
                        alt={buildAltText(item, item.images[0])}
                        style={{
                          width: '100%',
                          height: '300px',
                          objectFit: 'cover',
                          backgroundColor: '#f5f5f5',
                          display: 'block'
                        }}
                      />
                    )}
                    {isVideo(item.images[0]) && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '60px',
                        height: '60px',
                        background: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                      }}>
                        <span style={{ color: 'white', fontSize: '24px', marginLeft: '4px' }}>▶</span>
                      </div>
                    )}
                    {item.images.length > 1 && (
                      <div style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backdropFilter: 'blur(4px)'
                      }}>
                        {item.images.length} {item.images.some(img => isVideo(img)) ? 'items' : 'photos'}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ padding: '15px 20px 20px' }}>
                  <h3 style={{ marginBottom: '8px', fontSize: '18px' }}>{item.title}</h3>
                  {item.description && (
                    <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px', lineHeight: '1.5' }}>
                      {item.description?.substring(0, 100)}{item.description?.length > 100 ? '...' : ''}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {item.category && (
                      <span style={{
                        padding: '4px 12px',
                        background: '#f0f0f0',
                        borderRadius: '15px',
                        fontSize: '12px',
                        textTransform: 'capitalize'
                      }}>
                        {item.category}
                      </span>
                    )}
                    {item.tags && item.tags.slice(0, 2).map((tag, index) => (
                      <span key={index} style={{
                        padding: '4px 12px',
                        background: '#e8f0fe',
                        color: '#1967d2',
                        borderRadius: '15px',
                        fontSize: '12px'
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Overlay */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <div
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.92)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'lightboxFadeIn 0.25s ease'
          }}
          role="dialog"
          aria-modal="true"
          aria-label={`${lightboxTitle} - Image ${lightboxIndex + 1} of ${lightboxImages.length}`}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxOpen(false)}
            aria-label="Close lightbox"
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              color: 'white',
              fontSize: '28px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              transition: 'background 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            ✕
          </button>

          {/* Title and counter */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            color: 'white',
            zIndex: 10001
          }}>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>{lightboxTitle}</div>
            <div style={{ fontSize: '13px', opacity: 0.7 }}>
              {lightboxIndex + 1} / {lightboxImages.length}
            </div>
          </div>

          {/* Previous button */}
          {lightboxImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + lightboxImages.length) % lightboxImages.length); }}
              aria-label="Previous image"
              style={{
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.12)',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001,
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            >
              ‹
            </button>
          )}

          {/* Main media */}
          {(() => {
            const currentMedia = lightboxImages[lightboxIndex];
            if (currentMedia.embedUrl) {
              // Embedded video (YouTube/Vimeo)
              return (
                <div onClick={(e) => e.stopPropagation()} style={{ animation: 'lightboxImageIn 0.2s ease' }}>
                  <iframe
                    key={lightboxIndex}
                    src={getEmbedUrl(currentMedia.embedUrl)}
                    title={`${lightboxTitle} - Video`}
                    style={{
                      width: 'min(90vw, 960px)',
                      height: 'min(70vh, 540px)',
                      border: 'none',
                      borderRadius: '4px'
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  {currentMedia.downloadUrl && (
                    <div style={{ textAlign: 'center', marginTop: '12px' }}>
                      <a
                        href={currentMedia.downloadUrl}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: 'white',
                          background: 'rgba(255,255,255,0.15)',
                          padding: '8px 20px',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        ⬇ Download Video
                      </a>
                    </div>
                  )}
                </div>
              );
            } else if (isVideo(currentMedia)) {
              // Direct video file
              return (
                <video
                  key={lightboxIndex}
                  src={getImageUrl(currentMedia)}
                  controls
                  autoPlay
                  playsInline
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    maxWidth: 'calc(100vw - 120px)',
                    maxHeight: 'calc(100vh - 140px)',
                    borderRadius: '4px',
                    animation: 'lightboxImageIn 0.2s ease'
                  }}
                />
              );
            } else {
              return (
                <img
                  src={getImageUrl(currentMedia)}
                  alt={buildAltText(lightboxItem, currentMedia, `photo ${lightboxIndex + 1}`)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    maxWidth: 'calc(100vw - 120px)',
                    maxHeight: 'calc(100vh - 140px)',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    userSelect: 'none',
                    animation: 'lightboxImageIn 0.2s ease'
                  }}
                />
              );
            }
          })()}

          {/* Next button */}
          {lightboxImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % lightboxImages.length); }}
              aria-label="Next image"
              style={{
                position: 'absolute',
                right: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.12)',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001,
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            >
              ›
            </button>
          )}

          {/* Thumbnail strip */}
          {lightboxImages.length > 1 && (
            <div style={{
              position: 'absolute',
              bottom: '15px',
              display: 'flex',
              gap: '8px',
              padding: '8px 12px',
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '8px',
              maxWidth: 'calc(100vw - 40px)',
              overflowX: 'auto',
              backdropFilter: 'blur(8px)'
            }}>
              {lightboxImages.map((img, idx) => (
                <img
                  key={idx}
                  src={getImageUrl(img)}
                  alt={buildAltText(lightboxItem, img, `thumbnail ${idx + 1}`)}
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); }}
                  style={{
                    width: '56px',
                    height: '40px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    opacity: idx === lightboxIndex ? 1 : 0.5,
                    border: idx === lightboxIndex ? '2px solid white' : '2px solid transparent',
                    transition: 'opacity 0.2s, border-color 0.2s',
                    flexShrink: 0
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox CSS animations */}
      <style>{`
        @keyframes lightboxFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes lightboxImageIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

export default Portfolio;
